import { color, Text } from "@smoothincorg/smooth-ui"
import React, { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { HiExternalLink } from "react-icons/hi"
import { BorderedButton } from "src/components/Button"

import { InputField } from "src/components/InputField"
import { formatDate } from "src/components/List/List"
import { SubmitButton } from "src/features/lifftter/components/SubmitButton"
import { MovingApplication } from "src/hooks/useAdoptedMovingApplicationSWR"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { camelCase } from "src/lib/aikagi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { V1_OPE_URL } from "src/lib/constants/value"
import useSWR from "swr"
import { useEditingId } from "../../hooks/useEditingId"
import { ModalEditableForm } from "../ModalEditableForm/ModalEditableForm"
import { StatusEditor, StatusItems } from "../Proposal/Proposal"
import { sortById } from "../User/User"
import * as styles from "./MovingApplications.css"
import dayjs from "dayjs"
import { MainExaminations } from "./MainExaminations"
import { DestinationAddresses } from "./DestinationAddress"
import { useNotification } from "@smoothincorg/smooth-ui"
import { ReexamModal } from "./ReexamModal"

const defaultValues = {
  status: 0,
  adPrice: 0,
  rentPrice: 0,
  roughEstimateInitialCost: 0,
  houseName: "",
  roomName: "",
  applicationOn: "",
  movingTargetOn: "",
  paymentInitialCostOn: "",
  paymentInitialCostOnMemo: "",
  movingOn: "",
  initialCost: 0,
  version: 0,
  address: "",
  storeContractedOn: "",
  lendableLoan: 0,
  propertyId: null as number | null,
  totalInitialCost: 0,
}

const MovingApplicationItem: React.FC<{
  resourceName: string
  movingApplication?: MovingApplication
  proposalId: number
  mutateMovingApplications: () => Promise<void>
  canSelectReinspectionMode?: boolean
}> = (props) => {
  const [reinspectionMode, setReinspectionMode] = useState<"ON" | "OFF">("OFF")
  const [newScoresDefaultResult, setNewScoresDefaultResult] = useState<"要確認" | "空欄">("要確認")

  const { resourceName, movingApplication, proposalId } = props
  const { editingId } = useEditingId(resourceName)
  const shouldPatch = editingId > 0
  const { postWithToken, patchWithToken, deleteWithToken, getWithToken } = useSmoothApi()
  const { showAlert, showConfirm, showSnackbar } = useNotification()

  const data = movingApplication

  const movingApplicationForm = useForm<MovingApplication>({
    defaultValues,
  })
  const action = shouldPatch ? patchWithToken : postWithToken
  const endPoint = shouldPatch
    ? `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/moving_applications/${data?.id}`
    : `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/moving_applications`

  const statusValue = movingApplicationForm.watch("status")
  const applicationOnValue = movingApplicationForm.watch("applicationOn")
  const movingOnValue = movingApplicationForm.watch("movingOn")
  const movingTargetOnValue = movingApplicationForm.watch("movingTargetOn")
  const paymentInitialCostOnValue = movingApplicationForm.watch("paymentInitialCostOn")
  const storeContractedOnValue = movingApplicationForm.watch("storeContractedOn")

  const movingApplicationSubmit: SubmitHandler<MovingApplication> = async (data) => {
    const payload = {
      moving_application: {
        proposal_id: proposalId,
        status: data.status,
        ad_price: data.adPrice,
        rent_price: data.rentPrice,
        rough_estimate_initial_cost: data.roughEstimateInitialCost,
        house_name: data.houseName,
        room_name: data.roomName,
        payment_initial_cost_on_memo: data.paymentInitialCostOnMemo,
        initial_cost: data.initialCost,
        version: data.version,
        address: data.address,
        property_id: data.propertyId || undefined,
        total_initial_cost: data.totalInitialCost,
        moving_on: dayjs(data.movingOn).isValid() ? dayjs(data.movingOn).format("YYYY-MM-DD") : "",
        application_on: dayjs(data.applicationOn).isValid()
          ? dayjs(data.applicationOn).format("YYYY-MM-DD")
          : "",
        payment_initial_cost_on: dayjs(data.paymentInitialCostOn).isValid()
          ? dayjs(data.paymentInitialCostOn).format("YYYY-MM-DD")
          : "",
        moving_target_on: dayjs(data.movingTargetOn).isValid()
          ? dayjs(data.movingTargetOn).format("YYYY-MM-DD")
          : "",
        store_contracted_on: dayjs(data.storeContractedOn).isValid()
          ? dayjs(data.storeContractedOn).format("YYYY-MM-DD")
          : "",
      },
    }

    try {
      const res = await action(endPoint, payload)

      if (props.canSelectReinspectionMode && reinspectionMode === "ON") {
        const newMovingApplicationId = res.data["moving_application"]?.id
        await postWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/moving_applications/${newMovingApplicationId}/start_re_examination`,
          {}
        )

        const newMainExamination = await getWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/moving_applications/${newMovingApplicationId}/main_examinations`
        )

        const newLastMainExaminationId = newMainExamination.data["data"]?.[0]?.id

        const newScore = await getWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/main_examinations/${newLastMainExaminationId}/main_examination_score`
        )

        const newScoreId = newScore.data["data"]?.id

        const newScorePayload = {
          main_examination_score: {
            result: newScoresDefaultResult !== "空欄" ? newScoresDefaultResult : null,
          },
        }

        await patchWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/main_examination_scores/${newScoreId}`,
          newScorePayload
        )
      }
    } catch (e) {
      const error = e as Error
      showAlert("エラーが発生しました！: " + error.message)
      return
    }

    showSnackbar("保存しました！", "success")

    props.mutateMovingApplications()
  }

  const deleteSubmit = async (id: number) => {
    showConfirm("削除しますか？", async () => {
      try {
        await deleteWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/moving_applications/${id}`
        )
      } catch (e) {
        const error = e as Error
        showAlert("エラーが発生しました！: " + error.message)
      }
      showSnackbar("削除しました！", "success")

      props.mutateMovingApplications()
    })
  }

  useEffect(() => {
    if (data) {
      movingApplicationForm.reset((c) => {
        const d = data
        return {
          ...c,
          ...d,
          movingOn: dayjs(d.movingOn).isValid() ? dayjs(d.movingOn).format("YYYY-MM-DD") : "",
          applicationOn: dayjs(d.applicationOn).isValid()
            ? dayjs(d.applicationOn).format("YYYY-MM-DD")
            : "",
          paymentInitialCostOn: dayjs(d.paymentInitialCostOn).isValid()
            ? dayjs(d.paymentInitialCostOn).format("YYYY-MM-DD")
            : "",
          movingTargetOn: dayjs(d.movingTargetOn).isValid()
            ? dayjs(d.movingTargetOn).format("YYYY-MM-DD")
            : "",
          storeContractedOn: dayjs(d.storeContractedOn).isValid()
            ? dayjs(d.storeContractedOn).format("YYYY-MM-DD")
            : "",
        }
      })
    }

    if (editingId === 0) {
      movingApplicationForm.reset(defaultValues)
    }
  }, [movingApplicationForm, movingApplication, data, editingId])

  return (
    <ModalEditableForm
      resourceName={resourceName}
      resource={movingApplication}
      opeHref={V1_OPE_URL + "/admin/moving_applications/" + movingApplication?.id}
      items={[
        ["houseName", movingApplication?.houseName],
        [
          "ステータス",
          <StatusItems
            key={movingApplication?.id}
            targetStatus={movingApplication?.status}
            statuses={MOVING_APPLICATION_STATUSES}
          />,
        ],
        [
          "書類",
          <a
            key={movingApplication?.id}
            href={V1_OPE_URL + `/admin/moving_applications/${movingApplication?.id}/edit_documents`}
            target="_blank"
            style={{
              display: "flex",
              alignItems: "center",
              color: color.main,
              textDecoration: "underline",
            }}
          >
            書類を確認する{` `}
            <HiExternalLink />
          </a>,
        ],
        ["createdAt", formatDate(movingApplication?.createdAt)],
        ["updatedAt", formatDate(movingApplication?.updatedAt)],
      ]}
      onSubmit={(e) => console.log(e)}
      onDelete={deleteSubmit}
    >
      {props.canSelectReinspectionMode && (
        <div className={styles.field}>
          <h3 className={styles.h3}>再審査モード</h3>
          <div className={styles.flex}>
            {["ON", "OFF"].map((val) => (
              <label
                key={val}
                className={reinspectionMode === val ? styles.selectedLabel : styles.unselectedLabel}
              >
                <input
                  type="radio"
                  name="reinspectionMode"
                  value={val}
                  checked={reinspectionMode === val}
                  onChange={(e) => setReinspectionMode(e.target.value as "ON" | "OFF")}
                />
                {val}
              </label>
            ))}
          </div>
          {reinspectionMode === "ON" && (
            <div className={styles.flex}>
              {["要確認", "空欄"].map((val) => (
                <label
                  key={val}
                  className={
                    newScoresDefaultResult === val ? styles.selectedLabel : styles.unselectedLabel
                  }
                >
                  <input
                    type="radio"
                    name="newScoresDefaultResult"
                    value={val}
                    checked={newScoresDefaultResult === val}
                    onChange={(e) => setNewScoresDefaultResult(e.target.value as "要確認" | "空欄")}
                  />
                  {val}
                </label>
              ))}
            </div>
          )}
          {reinspectionMode === "ON" && (
            <p className={styles.paragraph}>
              ・再審査モードをONにすると、作成後、古いデータをキャンセル扱いにします。
              <br />
              ・また、作成後データの本審査情報（`MainExamination`）の**Cic examination**と**Smooth
              examination**を両方とも`なし`にします。
              <br />
              ・本審査結果（`MainExaminationScore`）のResultを
              {newScoresDefaultResult}にします。
            </p>
          )}
        </div>
      )}

      <StatusEditor
        label="ステータス（status）"
        statuses={MOVING_APPLICATION_STATUSES}
        value={statusValue}
        style={{ marginBottom: 30 }}
        onChange={(e) => {
          const currentStatus = statusValue
          const changedStatus = parseInt(e.target.value)
          const hasStatusAlready = currentStatus & changedStatus

          const newStatus = hasStatusAlready
            ? currentStatus - changedStatus
            : currentStatus + changedStatus

          movingApplicationForm.setValue("status", newStatus)
        }}
      />
      <InputField
        className={styles.field}
        label="申し込み物件の名前（houseName）"
        {...movingApplicationForm.register("houseName")}
      />
      <InputField
        className={styles.field}
        label="申し込んだ部屋の名前（roomName）"
        {...movingApplicationForm.register("roomName")}
      />
      <InputField
        className={styles.field}
        label="アドの金額（adPrice）"
        {...movingApplicationForm.register("adPrice")}
      />
      <InputField
        className={styles.field}
        label="家賃（rentPrice）"
        {...movingApplicationForm.register("rentPrice")}
      />
      <InputField
        className={styles.field}
        label="初期費用概算（roughEstimateInitialCost）"
        {...movingApplicationForm.register("roughEstimateInitialCost")}
      />
      <InputField
        className={styles.field}
        label="スムーズが立て替える金額（initialCost）"
        {...movingApplicationForm.register("initialCost")}
      />
      <InputField
        className={styles.field}
        label="初期費用総額（totalInitialCost）"
        {...movingApplicationForm.register("totalInitialCost")}
      />
      <InputField
        className={styles.field}
        label="入居申込日（applicationOn）"
        {...movingApplicationForm.register("applicationOn")}
        validationMessage={
          dayjs(applicationOnValue).isValid() ? (
            <Text size={14}>{dayjs(applicationOnValue).format("YYYY年MM月DD日")}</Text>
          ) : (
            "日付が認識できません"
          )
        }
      />
      <InputField
        className={styles.field}
        label="入居予定日（movingTargetOn）"
        {...movingApplicationForm.register("movingTargetOn")}
        validationMessage={
          dayjs(movingTargetOnValue).isValid() ? (
            <Text size={14}>{dayjs(movingTargetOnValue).format("YYYY年MM月DD日")}</Text>
          ) : (
            "日付が認識できません"
          )
        }
      />
      <InputField
        className={styles.field}
        label="初期費用支払い期日（paymentInitialCostOn）"
        {...movingApplicationForm.register("paymentInitialCostOn")}
        validationMessage={
          dayjs(paymentInitialCostOnValue).isValid() ? (
            <Text size={14}>{dayjs(paymentInitialCostOnValue).format("YYYY年MM月DD日")}</Text>
          ) : (
            "日付が認識できません"
          )
        }
      />
      <InputField
        className={styles.field}
        label="入居日（movingOn）"
        {...movingApplicationForm.register("movingOn")}
        validationMessage={
          dayjs(movingOnValue).isValid() ? (
            <Text size={14}>{dayjs(movingOnValue).format("YYYY年MM月DD日")}</Text>
          ) : (
            "日付が認識できません"
          )
        }
      />
      {/* <InputField
        className={styles.field}
        label="論理削除用のカラム"
        {...movingApplicationForm.register('enabled')}
      /> */}
      <InputField
        className={styles.field}
        label="賃貸借契約を結んだ日（storeContractedOn）"
        {...movingApplicationForm.register("storeContractedOn")}
        validationMessage={
          dayjs(storeContractedOnValue).isValid() ? (
            <Text size={14}>{dayjs(storeContractedOnValue).format("YYYY年MM月DD日")}</Text>
          ) : (
            "日付が認識できません"
          )
        }
      />
      <InputField
        className={styles.field}
        label="property_id"
        {...movingApplicationForm.register("propertyId", {
          valueAsNumber: true,
        })}
      />

      <SubmitButton
        onClick={async (e) => {
          e.preventDefault()
          const result = await movingApplicationForm.trigger()

          if (!result) {
            showAlert("エラーが発生しました！: 入力内容を確認してください")
          }

          if (result) {
            if (reinspectionMode === "ON") {
              const confirmed = window.confirm(
                "再審査の準備は画面左側の「再審査準備をする」ボタンから行うようにルールが変更されました。\nこのまま進めますか？"
              )
              if (confirmed) {
                movingApplicationForm.handleSubmit(movingApplicationSubmit)(e)
              }
            } else {
              movingApplicationForm.handleSubmit(movingApplicationSubmit)(e)
            }
          }
        }}
        style={{ width: 200, margin: "0 auto" }}
      >
        保存
      </SubmitButton>
    </ModalEditableForm>
  )
}
const MOVING_APPLICATION_STATUSES = [
  {
    id: 1,
    content: "application_document_uploaded",
    content_jp: "入居申し込み書類アップロード済み",
  },
  {
    id: 2,
    content: "summary_initial_cost_document_uploaded",
    content_jp: "初期費用見積書アップロード済み",
  },
  {
    id: 4,
    content: "maisoku_document_uploaded",
    content_jp: "マイソクアップロード済み",
  },
  { id: 8, content: "main_examination_applied", content_jp: "本審査開始" },
  {
    id: 16,
    content: "owner_guarantee_company_examination_finished",
    content_jp: "大家・保証会社の審査完了",
  },
  {
    id: 32,
    content: "smooth_main_examination_finished",
    content_jp: "smoothの本審査完了",
  },
  {
    id: 64,
    content: "fixed_initial_cost_document_uploaded",
    content_jp: "確定初期費用書アップロード済み",
  },
  {
    id: 128,
    content: "lease_contract_document_uploaded",
    content_jp: "賃貸借契約書アップロード済み",
  },
  { id: 256, content: null, content_jp: null },
  {
    id: 512,
    content: "important_points_explanation_document_uploaded",
    content_jp: "重要事項説明書アップロード済み",
  },
  {
    id: 1024,
    content: "important_points_explanation_finished",
    content_jp: "重要事項説明済み",
  },
  {
    id: 2048,
    content: "bank_account_registered",
    content_jp: "口座情報登録済",
  },
  { id: 4096, content: "contract_created", content_jp: "契約作成済み" },
  {
    id: 8192,
    content: "smooth_payment_applied",
    content_jp: "仲介業者の初期費用立替申請提出済み",
  },
  { id: 16384, content: null, content_jp: null },
  { id: 32768, content: "smooth_payment_done", content_jp: "初期費用立替済み" },
  {
    id: 65536,
    content: "store_confirmed_payment",
    content_jp: "初期費用立替確認済み",
  },
  { id: 131072, content: null, content_jp: null },
  {
    id: 262144,
    content: "maisoku_for_company_document_uploaded",
    content_jp: "マイソク原本アップロード済み",
  },
  { id: 524288, content: "cancel", content_jp: "キャンセル" },
  { id: 1_048_576, content: null, content_jp: null },
  { id: 2_097_152, content: null, content_jp: null },
  {
    id: 4_194_304,
    content: "smooth_payment_application_fixed",
    content_jp: "仲介業者の初期費用立替申請確定済み",
  },
  { id: 8_388_608, content: null, content_jp: null },
  { id: 16_777_216, content: "confirm", content_jp: "入居申込みを確認した" },
  { id: 33_554_432, content: null, content_jp: null },
  {
    id: 67_108_864,
    content: "accept_payment_promises",
    content_jp: "支払いスケジュールに同意した",
  },
]

export const MovingApplications: React.FC<{ proposalId: number }> = ({ proposalId }) => {
  const resourceName = `Proposal.${proposalId}.MovingApplications`
  const { setEditingId, editingId } = useEditingId(resourceName)
  const { getWithToken } = useSmoothApi()

  const { data, mutate } = useSWR(
    `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/moving_applications`,
    async (url) => {
      const response = await getWithToken(url)
      return response.data["data"].map((d: MovingApplication) =>
        camelCase(d)
      ) as MovingApplication[]
    }
  )

  const isCreatingNew = editingId === 0
  const canSelectReinspectionMode = isCreatingNew && data?.length > 0

  const [reexamModalOpen, setReexamModalOpen] = useState(false)
  const [reexamTargetId, setReexamTargetId] = useState<number | null>(null)

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>
        入居申込（{data?.length}）
        <BorderedButton
          onClick={(e) => {
            e.preventDefault()
            setEditingId((c) => {
              if (c === 0) return null
              return 0
            })
          }}
          style={{ marginLeft: "1rem", background: "#ffffff" }}
        >
          新規作成
        </BorderedButton>
      </h2>
      <section>
        {sortById(data || [])?.map((movingApplication) => {
          return (
            <div key={movingApplication.id} className={styles.movingApplication}>
              <MovingApplicationItem
                resourceName={resourceName}
                movingApplication={movingApplication}
                proposalId={proposalId}
                mutateMovingApplications={async () => {
                  mutate()
                }}
                canSelectReinspectionMode={canSelectReinspectionMode}
              />
              <div>
                <BorderedButton
                  style={{ background: "#fff", margin: 10 }}
                  onClick={() => {
                    setReexamTargetId(movingApplication.id)
                    setReexamModalOpen(true)
                  }}
                >
                  再審査準備をする
                </BorderedButton>
              </div>
              <DestinationAddresses movingApplicationId={movingApplication.id} />
              <MainExaminations movingApplicationId={movingApplication.id} />
            </div>
          )
        })}
        {!data?.length ? (
          <div className="not-found">
            登録されていません
            <MovingApplicationItem
              resourceName={resourceName}
              proposalId={proposalId}
              mutateMovingApplications={async () => {
                mutate()
              }}
            />
          </div>
        ) : null}
      </section>
      <ReexamModal
        open={reexamModalOpen}
        onClose={() => setReexamModalOpen(false)}
        movingApplicationId={reexamTargetId}
      />
    </div>
  )
}
